










































































































































































import { Component, Ref, Vue } from 'vue-property-decorator'
import { Source } from '@/api/model/source'
import { ReferralChannel, availableReferralChannels } from '@/api/types'
import { CompanyAPI, SourceAPI } from '@/api/backend-client'
import { v4 as uuid } from 'uuid'
import EditSourceForm from '@/components/form/EditSourceForm.vue'
import ListContainer from '@/components/ui/list/ListContainer.vue'
import ListItem, { ListItemClickEvent } from '@/components/ui/list/list-item/ListItem.vue'
import IconArrowDoubleRight from '@/components/icons/IconArrowDoubleRight.vue'
import { ListItemActionClickEvent } from '@/components/ui/list/list-item/ListItemAction.vue'

interface SourceOption {
    source: Source,
    isNew: boolean,
}

@Component({
    components: {
        IconArrowDoubleRight,
        EditSourceForm,
        ListContainer,
        ListItem,
    },
})
export default class Channels extends Vue {
    loadingSources = false
    showInactiveSources = false
    selectedChannel: ReferralChannel = null
    editingSource: Source = null
    sourceOptions: SourceOption[] = []
    @Ref('source-container') readonly sourceContainerElement!: HTMLElement

    mounted() {
        this.loadSources()
    }

    async loadSources(): Promise<void> {
        this.loadingSources = true
        this.editingSource = null

        this.sourceOptions = await SourceAPI.getAll({ company: this.$currentCompany.id }).then(sources => sources.map(source => ({ source, isNew: false })))
        this.sortSourceOptions()

        this.loadingSources = false
    }

    async reloadCompany(): Promise<void> {
        this.loadingSources = true
        await CompanyAPI.reloadCurrentCompany()
        this.loadingSources = false
    }

    async createSource(): Promise<void> {
        if (!this.selectedChannel) {
            return
        }

        const source = Source.fromData({
            company: this.$currentCompany.resourceIdentifier,
            id: uuid(),
            name: '',
            active: true,
            channel: this.selectedChannel,
        })

        this.sourceOptions.push({ source, isNew: true })
        this.sortSourceOptions()
        this.editingSource = source
    }

    fetchSourcesForChannel(channel: string): Source[] {
        return this.sourceOptions
            .filter(option => !option.isNew)
            .map(option => option.source)
            .filter(source => source.channel === channel)
    }

    sourceStatus(option: SourceOption) {
        if (option.isNew) {
            return {
                label: this.$t('channelsAndSources.source.new').toString(),
                color: 'orange',
                icon: 'IconWarning',
            }
        }

        if (option.source.active === false) {
            return {
                label: this.$t('channelsAndSources.source.inactive').toString(),
                color: 'red',
                icon: 'IconDot',
            }
        }

        return {
            label: this.$t('channelsAndSources.source.active').toString(),
            color: 'green',
            icon: 'IconDot',
        }
    }

    isEditable(item: SourceOption) {
        return item.source.isEditable
    }

    onChannelClicked(event: ListItemClickEvent<ReferralChannel>): void {
        if (event.item === this.selectedChannel) {
            return
        }

        this.selectedChannel = event.item
    }

    onSourceClicked(event: ListItemClickEvent<SourceOption>): void {
        if (event.item.source.isStaticSource) {
            return
        }

        this.editingSource = event.item.source
    }

    onSourceActionClicked(event: ListItemActionClickEvent<SourceOption>): void {
        this.editingSource = event.item.source
    }

    async onSourceSaved(value: Source): Promise<void> {
        this.editingSource = null
        await this.loadSources()
        await this.reloadCompany()
    }

    async onSourceCanceled(value: Source): Promise<void> {
        this.editingSource = null

        // Remove inserted source if it was newly created
        const option = this.sourceOptions.find(sourceOption => sourceOption.source.id === value.id)
        if (option?.isNew) {
            this.sourceOptions = this.sourceOptions
                .filter(filterOption => filterOption.source.id !== value.id)
        }
    }

    async onSourceDeleted(value: Source): Promise<void> {
        this.editingSource = null
        await this.loadSources()
        await this.reloadCompany()
    }

    get availableChannels(): ReferralChannel[] {
        return availableReferralChannels()
    }

    get availableSourceOptions(): SourceOption[] {
        let sourceOptions = this.sourceOptions
            .filter(option => option.source.channel === this.selectedChannel)

        if (!this.showInactiveSources) {
            sourceOptions = sourceOptions.filter(option => option.isNew || option.source.active)
        }

        return sourceOptions
    }


    get separatorOrigin(): null|HTMLElement {
        if (!this.selectedChannel) {
            return null
        }

        return document.getElementById('channel-' + this.selectedChannel)
    }

    private sortSourceOptions(): void {
        this.sourceOptions
        .sort((a, b) => a.source.name.localeCompare(b.source.name))
        .sort((a, b) => a.source.isStaticSource === b.source.isStaticSource ? 0 : a.source.isStaticSource ? -1 : 1)
    }
}
